/**
 * PageUnblockerConfig Component
 *
 * Simplified configuration for handling popups, captchas, and access blocks.
 * Uses category-level toggles with expandable customization for power users.
 *
 * Adapted for web app: Uses localStorage instead of chrome.storage.local
 */

import React, { useState, useEffect } from "react";
import { HiChevronDown, HiChevronUp } from "react-icons/hi2";
import { FaCookieBite, FaPuzzlePiece, FaCog, FaEye } from "react-icons/fa";
import { IoClose } from "react-icons/io5";

const PAGE_UNBLOCKER_PREFS_KEY = "cloudPageUnblockerPrefs";
const PAGE_UNBLOCKER_ONBOARDING_KEY = "cloudPageUnblockerOnboardingDismissed";

// Overlay/popup types (for customization)
const OVERLAY_TYPES = [
  { id: "consent_banner", label: "Cookie Banners", emoji: "🍪" },
  { id: "newsletter_popup", label: "Newsletter Popups", emoji: "📧" },
  { id: "country_chooser", label: "Country/Region Chooser", emoji: "🌍", hasInput: true },
  { id: "age_verification", label: "Age Verification", emoji: "🔞" },
  { id: "other_popup", label: "Other Popups", emoji: "🔲" },
];

// Captcha types - only cloudflare_turnstile is solvable currently
const CAPTCHA_TYPES = [
  { id: "cloudflare_turnstile", label: "Cloudflare Turnstile", emoji: "☁️", solvable: true },
  { id: "datadome", label: "DataDome", emoji: "🛡️", solvable: false },
  { id: "recaptcha_v2", label: "reCAPTCHA v2", emoji: "🔲", solvable: false },
  { id: "recaptcha_v3", label: "reCAPTCHA v3", emoji: "🔳", solvable: false },
  { id: "hcaptcha", label: "hCaptcha", emoji: "🧩", solvable: false },
  { id: "custom_captcha", label: "Custom Captcha", emoji: "❓", solvable: false },
];

// Simple tooltip component - position: "center" (default), "left", "right"
const Tooltip = ({ children, text, position = "center" }) => {
  const positionClasses = {
    center: "left-1/2 -translate-x-1/2",
    left: "left-0",
    right: "right-0",
  };

  return (
    <div className="relative group">
      {children}
      <div
        className={`absolute bottom-full ${positionClasses[position]} mb-1.5 px-2 py-1 text-2xs text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none whitespace-nowrap z-50`}
      >
        {text}
      </div>
    </div>
  );
};

// Toggle switch component
const Toggle = ({ enabled, onChange, disabled = false }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!enabled)}
    disabled={disabled}
    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
      disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
    } ${enabled ? "bg-emerald-500" : "bg-gray-300 dark:bg-gray-600"}`}
  >
    <span
      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
        enabled ? "translate-x-5" : "translate-x-1"
      }`}
    />
  </button>
);

// Simple behavior selector (3 options in a compact row)
// handleLabel: "Dismiss" for popups, "Solve" for captchas
const BehaviorSelector = ({ value, onChange, canHandle = true, handleLabel = "Dismiss" }) => {
  const options = [
    {
      value: "handle",
      label: handleLabel,
      color: "emerald",
      disabled: !canHandle,
      tooltip: handleLabel === "Solve"
        ? "Automatically solve this captcha when detected"
        : "Automatically dismiss this popup when detected",
    },
    {
      value: "warn",
      label: "Ignore",
      color: "amber",
      tooltip: "Continue automation even if detected",
    },
    {
      value: "error",
      label: "Stop",
      color: "red",
      tooltip: "Stop the automation immediately if detected",
    },
  ];

  return (
    <div className="flex gap-1">
      {options.map((opt, index) => (
        <Tooltip key={opt.value} text={opt.tooltip} position={index === 0 ? "center" : "right"}>
          <button
            onClick={() => !opt.disabled && onChange(opt.value)}
            disabled={opt.disabled}
            className={`px-2 py-0.5 text-2xs font-medium rounded transition-colors ${
              opt.disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"
            } ${
              value === opt.value
                ? opt.color === "emerald"
                  ? "bg-emerald-100 dark:bg-emerald-500/30 text-emerald-600 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-500/50"
                  : opt.color === "amber"
                  ? "bg-amber-100 dark:bg-amber-500/30 text-amber-600 dark:text-amber-300 border border-amber-300 dark:border-amber-500/50"
                  : "bg-red-100 dark:bg-red-500/30 text-red-600 dark:text-red-300 border border-red-300 dark:border-red-500/50"
                : "bg-gray-100 dark:bg-gray-700/30 text-gray-500 dark:text-gray-400 border border-transparent hover:bg-gray-200 dark:hover:bg-gray-700/50"
            }`}
          >
            {opt.label}
          </button>
        </Tooltip>
      ))}
    </div>
  );
};

const PageUnblockerConfig = ({ config, onChange }) => {
  const [showPopupDetails, setShowPopupDetails] = useState(true);
  const [showCaptchaDetails, setShowCaptchaDetails] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Check onboarding state on mount
  useEffect(() => {
    if (!localStorage.getItem(PAGE_UNBLOCKER_ONBOARDING_KEY)) {
      setShowOnboarding(true);
    }
  }, []);

  const dismissOnboarding = () => {
    setShowOnboarding(false);
    try {
      localStorage.setItem(PAGE_UNBLOCKER_ONBOARDING_KEY, "true");
    } catch (err) {
      console.error("Failed to save onboarding dismissal:", err);
    }
  };

  // Save user preferences to localStorage
  const saveUserPrefs = (newConfig) => {
    try {
      localStorage.setItem(PAGE_UNBLOCKER_PREFS_KEY, JSON.stringify(newConfig));
    } catch (err) {
      console.error("Failed to save page unblocker preferences:", err);
    }
  };

  // Get behavior for a specific subtype
  const getBehavior = (subtype) => {
    return config?.overrides?.[subtype] ?? "handle";
  };

  // Toggle entire category
  const toggleCategory = (items, enable, defaultOn = "handle", defaultOff = "warn") => {
    const newOverrides = { ...config?.overrides };
    items.forEach((item) => {
      // For unsolvable captchas, don't set to "handle"
      if (!enable || (item.solvable !== false)) {
        newOverrides[item.id] = enable ? defaultOn : defaultOff;
      } else {
        newOverrides[item.id] = "error"; // Unsolvable captchas default to error when "enabled"
      }
    });
    const newConfig = { ...config, overrides: newOverrides };
    onChange(newConfig);
    saveUserPrefs(newConfig);
  };

  // Update override for a specific subtype
  const handleOverrideChange = (subtype, behavior) => {
    const newConfig = {
      ...config,
      overrides: { ...config?.overrides, [subtype]: behavior },
    };
    onChange(newConfig);
    saveUserPrefs(newConfig);
  };

  // Update handler input (e.g., country for country_chooser)
  const handleInputChange = (subtype, field, value) => {
    const newConfig = {
      ...config,
      handlerInputs: {
        ...config?.handlerInputs,
        [subtype]: { ...config?.handlerInputs?.[subtype], [field]: value },
      },
    };
    onChange(newConfig);
    saveUserPrefs(newConfig);
  };

  // Count how many items are set to "handle" in a category
  const getEnabledCount = (items) => {
    return items.filter((item) => getBehavior(item.id) === "handle").length;
  };

  const popupsEnabled = getEnabledCount(OVERLAY_TYPES) > 0;
  const captchasEnabled = getEnabledCount(CAPTCHA_TYPES.filter((c) => c.solvable)) > 0;

  return (
    <div className="space-y-2.5">
      {/* Onboarding Overlay */}
      {showOnboarding && (
        <div className="relative rounded-xl overflow-hidden border border-violet-200 dark:border-violet-500/20 bg-gradient-to-br from-white dark:from-gray-900/80 via-gray-50 dark:via-gray-900/70 to-violet-50 dark:to-violet-950/40 backdrop-blur-md shadow-xl">
          {/* Close button */}
          <button
            onClick={dismissOnboarding}
            className="absolute top-2 right-2 z-10 w-6 h-6 rounded-lg flex items-center justify-center
                       bg-gray-100 dark:bg-gray-800/60 border border-gray-300 dark:border-gray-600/40 hover:bg-gray-200 dark:hover:bg-gray-700/80 hover:border-gray-400 dark:hover:border-gray-500/50
                       transition-all duration-200"
          >
            <IoClose className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200" />
          </button>

          <div className="px-4 pt-4 pb-3">
            {/* Illustration */}
            <div className="flex justify-center mb-3">
              <div className="relative">
                <div className="absolute inset-0 bg-violet-500/10 rounded-full blur-2xl scale-150" />
                {/* Mini browser frame */}
                <div className="relative w-52 h-40 rounded-xl bg-white dark:bg-gray-900/80 border border-gray-300 dark:border-gray-600/40 shadow-xl overflow-hidden flex flex-col">
                  {/* Browser toolbar */}
                  <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-100 dark:bg-gray-800/90 border-b border-gray-200 dark:border-gray-700/50">
                    <div className="flex gap-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-400/60" />
                      <div className="w-1.5 h-1.5 rounded-full bg-yellow-400/60" />
                      <div className="w-1.5 h-1.5 rounded-full bg-green-400/60" />
                    </div>
                    <div className="flex-1 flex items-center gap-1 mx-1 px-2 py-0.5 bg-gray-200 dark:bg-gray-700/50 rounded-md">
                      <div className="w-2 h-2 rounded-full bg-emerald-400/50" />
                      <div className="w-14 h-1 bg-gray-300 dark:bg-gray-500/30 rounded-full" />
                    </div>
                  </div>
                  {/* Page content with overlay popups */}
                  <div className="relative p-2 flex-1">
                    {/* Background "page" content lines */}
                    <div className="space-y-1.5 opacity-40">
                      <div className="w-full h-1 bg-gray-300 dark:bg-gray-600/40 rounded-full" />
                      <div className="w-3/4 h-1 bg-gray-300 dark:bg-gray-600/40 rounded-full" />
                      <div className="w-full h-1 bg-gray-300 dark:bg-gray-600/40 rounded-full" />
                      <div className="w-2/3 h-1 bg-gray-300 dark:bg-gray-600/40 rounded-full" />
                      <div className="w-5/6 h-1 bg-gray-300 dark:bg-gray-600/40 rounded-full" />
                      <div className="w-full h-1 bg-gray-300 dark:bg-gray-600/40 rounded-full" />
                      <div className="w-3/5 h-1 bg-gray-300 dark:bg-gray-600/40 rounded-full" />
                    </div>

                    {/* Captcha overlay - centered in page */}
                    <div className="absolute top-3 left-0 right-0 flex justify-center">
                      <div className="relative w-24 h-10 bg-white dark:bg-gray-800/95 border border-violet-300 dark:border-violet-500/30 rounded-md overflow-hidden shadow-lg">
                        <div className="flex items-center gap-1.5 px-2 py-2">
                          <div className="w-4 h-4 rounded bg-emerald-100 dark:bg-emerald-500/25 border border-emerald-300 dark:border-emerald-400/50 flex items-center justify-center flex-shrink-0">
                            <svg
                              className="w-2.5 h-2.5 text-emerald-500 dark:text-emerald-300"
                              viewBox="0 0 12 12"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M2 6.5L5 9.5L10 3" />
                            </svg>
                          </div>
                          <span className="text-[7px] text-gray-500 dark:text-gray-300/90 font-medium leading-tight">captcha</span>
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-violet-400/10 to-transparent animate-pulse" />
                      </div>
                    </div>

                    {/* Cookie banner overlay - bottom */}
                    <div className="absolute bottom-1.5 left-1.5 right-1.5 p-1.5 bg-amber-100 dark:bg-amber-900/60 border border-amber-300 dark:border-amber-500/30 rounded-md flex items-center gap-1.5 opacity-60">
                      <span className="text-[6px] text-amber-700 dark:text-amber-200/80 leading-none">We use cookies...</span>
                      <div className="ml-auto w-5 h-2.5 rounded-sm bg-amber-300 dark:bg-amber-500/40 border border-amber-400 dark:border-amber-500/50" />
                    </div>

                    {/* Dismiss sweep effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-emerald-400/8 to-transparent animate-pulse" />
                  </div>
                </div>

                {/* Checkmark badge */}
                <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-emerald-500/90 border-2 border-white dark:border-gray-900 flex items-center justify-center shadow-md">
                  <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2 6.5L5 9.5L10 3" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="text-center px-2">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">Auto-Handle Page Blockers</h3>
              <p className="text-xs text-gray-500 leading-relaxed">Automatically dismiss cookie banners, close popups, and solve captchas so your cloud extraction runs without interruption.</p>
            </div>
          </div>
        </div>
      )}

      {/* Popups & Banners Section */}
      <div className="bg-white dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700/30 rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200 dark:border-gray-700/30">
          <div className="flex items-center">
            <div className="p-1.5 rounded-lg bg-amber-100 dark:bg-amber-500/15 mr-2">
              <FaCookieBite className="text-amber-500 dark:text-amber-400 w-3 h-3" />
            </div>
            <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wide">Popups & Banners</h3>
          </div>
          <Toggle enabled={popupsEnabled} onChange={(v) => toggleCategory(OVERLAY_TYPES, v)} />
        </div>

        <div className="space-y-1">
          {/* Expand/collapse customization */}
          <button
            onClick={() => setShowPopupDetails(!showPopupDetails)}
            className="w-full flex items-center justify-between py-1.5 px-2 rounded-lg text-2xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors"
          >
            <span>Customize individual popup types</span>
            {showPopupDetails ? <HiChevronUp className="w-3 h-3" /> : <HiChevronDown className="w-3 h-3" />}
          </button>

          {showPopupDetails && (
            <div className="space-y-0.5 pt-1">
              {OVERLAY_TYPES.map((overlay) => {
                const behavior = getBehavior(overlay.id);
                return (
                  <div key={overlay.id}>
                    <div className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors">
                      <div className="flex items-center gap-2">
                        <span className="text-xs opacity-70">{overlay.emoji}</span>
                        <span className="text-sm text-gray-700 dark:text-gray-300">{overlay.label}</span>
                      </div>
                      <BehaviorSelector value={behavior} onChange={(v) => handleOverrideChange(overlay.id, v)} handleLabel="Dismiss" />
                    </div>
                    {/* Country input for country_chooser when set to handle */}
                    {overlay.hasInput && behavior === "handle" && overlay.id === "country_chooser" && (
                      <div className="ml-8 mr-2 mb-2">
                        <input
                          type="text"
                          value={config?.handlerInputs?.country_chooser?.country || ""}
                          onChange={(e) => handleInputChange("country_chooser", "country", e.target.value)}
                          placeholder="Country to select (e.g., United States)"
                          className="w-full px-2.5 py-1.5 text-xs bg-gray-50 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700/40 rounded-lg text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-amber-500/30"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Captcha Solving Section */}
      <div className="bg-white dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700/30 rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200 dark:border-gray-700/30">
          <div className="flex items-center">
            <div className="p-1.5 rounded-lg bg-violet-100 dark:bg-violet-500/15 mr-2">
              <FaPuzzlePiece className="text-violet-500 dark:text-violet-400 w-3 h-3" />
            </div>
            <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wide">Captcha Solving</h3>
          </div>
          <Toggle
            enabled={captchasEnabled}
            onChange={(v) => {
              // Only toggle the solvable captcha
              handleOverrideChange("cloudflare_turnstile", v ? "handle" : "error");
            }}
          />
        </div>

        <div className="space-y-1">
          {/* Expand/collapse customization */}
          <button
            onClick={() => setShowCaptchaDetails(!showCaptchaDetails)}
            className="w-full flex items-center justify-between py-1.5 px-2 rounded-lg text-2xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors"
          >
            <span>Customize captcha handling</span>
            {showCaptchaDetails ? <HiChevronUp className="w-3 h-3" /> : <HiChevronDown className="w-3 h-3" />}
          </button>

          {showCaptchaDetails && (
            <div className="space-y-0.5 pt-1">
              {CAPTCHA_TYPES.map((captcha) => (
                <div key={captcha.id} className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors">
                  <div className="flex items-center gap-2">
                    <span className="text-xs opacity-70">{captcha.emoji}</span>
                    <span className={`text-sm ${captcha.solvable ? "text-gray-700 dark:text-gray-300" : "text-gray-400 dark:text-gray-500"}`}>
                      {captcha.label}
                      {!captcha.solvable && <span className="ml-1.5 text-2xs text-gray-400 dark:text-gray-600">(unsupported)</span>}
                    </span>
                  </div>
                  <BehaviorSelector value={getBehavior(captcha.id)} onChange={(v) => handleOverrideChange(captcha.id, v)} canHandle={captcha.solvable} handleLabel="Solve" />
                </div>
              ))}
              <p className="text-2xs text-gray-400 dark:text-gray-600 text-center pt-2">More captcha support coming soon</p>
            </div>
          )}
        </div>
      </div>

      {/* AI Detection Mode Section */}
      <div className="bg-white dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700/30 rounded-2xl p-4 shadow-sm">
        <div className="flex items-center mb-3 pb-2 border-b border-gray-200 dark:border-gray-700/30">
          <div className="p-1.5 rounded-lg bg-blue-100 dark:bg-blue-500/15 mr-2">
            <FaEye className="text-blue-500 dark:text-blue-400 w-3 h-3" />
          </div>
          <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wide">AI Detection Mode</h3>
        </div>

        <div className="flex flex-col gap-2 px-1">
          {/* Image mode */}
          <button
            onClick={() => {
              const newConfig = { ...config, detectionMode: "image" };
              onChange(newConfig);
              saveUserPrefs(newConfig);
            }}
            className={`relative w-full text-left p-3 pl-10 rounded-xl transition-all duration-200 ${
              (config?.detectionMode ?? "image") === "image"
                ? "bg-blue-50 dark:bg-blue-500/15 border border-blue-400 dark:border-blue-500/50 shadow-sm dark:shadow-[0_0_12px_-3px_rgba(59,130,246,0.3)]"
                : "bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/40 hover:border-gray-300 dark:hover:border-gray-600/60 hover:bg-gray-100 dark:hover:bg-gray-800/60"
            }`}
          >
            <div
              className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 transition-all duration-200 flex items-center justify-center ${
                (config?.detectionMode ?? "image") === "image" ? "border-blue-500 dark:border-blue-400 bg-blue-100 dark:bg-blue-500/20" : "border-gray-400 dark:border-gray-500 bg-transparent"
              }`}
            >
              {(config?.detectionMode ?? "image") === "image" && <div className="w-2 h-2 rounded-full bg-blue-500 dark:bg-blue-400" />}
            </div>
            <div className="flex items-center justify-between mb-1">
              <span className={`text-sm font-medium ${(config?.detectionMode ?? "image") === "image" ? "text-gray-900 dark:text-white" : "text-gray-700 dark:text-gray-300"}`}>📸 Image</span>
            </div>
            <p className={`text-2xs leading-relaxed ${(config?.detectionMode ?? "image") === "image" ? "text-gray-600 dark:text-gray-300" : "text-gray-500"}`}>
              Uses page screenshots to visually detect blockers like captchas or popups. Fast and reliable.
            </p>
          </button>
          {/* Text mode */}
          <button
            onClick={() => {
              const newConfig = { ...config, detectionMode: "text" };
              onChange(newConfig);
              saveUserPrefs(newConfig);
            }}
            className={`relative w-full text-left p-3 pl-10 rounded-xl transition-all duration-200 ${
              config?.detectionMode === "text"
                ? "bg-blue-50 dark:bg-blue-500/15 border border-blue-400 dark:border-blue-500/50 shadow-sm dark:shadow-[0_0_12px_-3px_rgba(59,130,246,0.3)]"
                : "bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/40 hover:border-gray-300 dark:hover:border-gray-600/60 hover:bg-gray-100 dark:hover:bg-gray-800/60"
            }`}
          >
            <div
              className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 transition-all duration-200 flex items-center justify-center ${
                config?.detectionMode === "text" ? "border-blue-500 dark:border-blue-400 bg-blue-100 dark:bg-blue-500/20" : "border-gray-400 dark:border-gray-500 bg-transparent"
              }`}
            >
              {config?.detectionMode === "text" && <div className="w-2 h-2 rounded-full bg-blue-500 dark:bg-blue-400" />}
            </div>
            <div className="flex items-center justify-between mb-1">
              <span className={`text-sm font-medium ${config?.detectionMode === "text" ? "text-gray-900 dark:text-white" : "text-gray-700 dark:text-gray-300"}`}>📝 Text</span>
            </div>
            <p className={`text-2xs leading-relaxed ${config?.detectionMode === "text" ? "text-gray-600 dark:text-gray-300" : "text-gray-500"}`}>
              Uses website HTML and text content to detect blockers. Has better context when dealing with captchas.
            </p>
          </button>
        </div>
      </div>

      {/* Advanced Settings Section */}
      <div className="bg-white dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700/30 rounded-2xl p-4 shadow-sm">
        <div className="flex items-center mb-3 pb-2 border-b border-gray-200 dark:border-gray-700/30">
          <div className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-500/15 mr-2">
            <FaCog className="text-gray-500 dark:text-gray-400 w-3 h-3" />
          </div>
          <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wide">Advanced Settings</h3>
        </div>

        <div className="space-y-0.5">
          {/* Max Blockers Per Page */}
          <div className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Max Blockers Per Page</label>
              <p className="text-2xs text-gray-400 dark:text-gray-500">Max blockers to handle on a single page</p>
            </div>
            <input
              type="number"
              min="1"
              max="3"
              value={config?.maxBlockersToProcess ?? 2}
              onChange={(e) => {
                const val = Math.max(1, Math.min(3, parseInt(e.target.value, 10) || 1));
                const newConfig = { ...config, maxBlockersToProcess: val };
                onChange(newConfig);
                saveUserPrefs(newConfig);
              }}
              className="w-14 px-2 py-1 text-xs text-center bg-gray-50 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700/40 rounded-lg text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500/30"
            />
          </div>
          {/* Delay Between Attempts */}
          <div className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Delay Between Attempts</label>
              <p className="text-2xs text-gray-400 dark:text-gray-500">Wait time after handling a blocker (ms)</p>
            </div>
            <input
              type="number"
              min="500"
              max="10000"
              step="500"
              value={config?.minDelayBetweenAttempts ?? 2000}
              onChange={(e) => {
                const val = Math.max(500, Math.min(10000, parseInt(e.target.value, 10) || 500));
                const newConfig = { ...config, minDelayBetweenAttempts: val };
                onChange(newConfig);
                saveUserPrefs(newConfig);
              }}
              className="w-16 px-2 py-1 text-xs text-center bg-gray-50 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700/40 rounded-lg text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500/30"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageUnblockerConfig;
